// 날씨 상세 모달 — 현재 상세 + 향후 24시간 시간별(기온 라인 + 표)
import React, { useEffect } from 'react';
import {
  ComposedChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import type { WeatherSnapshot, WeatherCode } from '@grosync/common';
import { Sym } from '../../ui';

const CODE_SYM: Record<WeatherCode, string> = {
  sunny: 'sunny',
  partly_cloudy_day: 'partly_cloudy_day',
  cloudy: 'cloudy',
  rainy: 'rainy',
  snowy: 'weather_snowy',
  thunderstorm: 'thunderstorm',
};

function hourLabel(iso: string): string {
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2, '0')}시`;
}

export default function WeatherDetailModal({
  snapshot, regionName, onClose,
}: { snapshot: WeatherSnapshot; regionName: string; onClose: () => void }): React.ReactElement {
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const { now, hourly } = snapshot;
  const rows = (hourly ?? []).map((h) => ({ ...h, label: hourLabel(h.time) }));

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 'min(720px, 96vw)', maxHeight: '90vh', overflowY: 'auto',
          background: 'var(--md-sys-color-surface-container-lowest)', borderRadius: 20,
          border: '1px solid var(--md-sys-color-outline-variant)', padding: 20,
          animation: 'modalIn 160ms ease', display: 'flex', flexDirection: 'column', gap: 16,
        }}
      >
        {/* header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Sym name={CODE_SYM[now.code]} size={36} fill={1} style={{ color: 'var(--md-sys-color-primary)' }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ font: '700 18px/22px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface)' }}>
              {regionName}
            </div>
            <div style={{ font: '500 13px/16px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface-variant)' }}>
              {now.desc} · <span className="gs-num">{now.temp}°</span>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              width: 36, height: 36, borderRadius: 9999, border: 0, cursor: 'pointer',
              background: 'var(--md-sys-color-surface-container-high)', display: 'inline-flex',
              alignItems: 'center', justifyContent: 'center', color: 'var(--md-sys-color-on-surface)',
            }}
          >
            <Sym name="close" size={20} />
          </button>
        </div>

        {/* current detail */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
          <DetailStat icon="device_thermostat" label="최고 / 최저" value={`${now.hi}° / ${now.lo}°`} />
          <DetailStat icon="water_drop" label="현재" value={`${now.temp}°`} />
          <DetailStat icon="thermostat" label="날씨" value={now.desc} />
        </div>

        {/* hourly */}
        <div style={{ font: '700 14px/18px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface)' }}>
          향후 24시간
        </div>
        {rows.length === 0 ? (
          <div style={{ padding: '40px 0', textAlign: 'center', font: '400 13px/18px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface-variant)' }}>
            시간별 예보 정보가 없습니다.
          </div>
        ) : (
          <>
            <ResponsiveContainer width="100%" height={200}>
              <ComposedChart data={rows} margin={{ top: 12, right: 12, bottom: 4, left: 0 }}>
                <CartesianGrid vertical={false} stroke="var(--md-sys-color-outline-variant)" strokeDasharray="3 4" />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: 'var(--md-sys-color-on-surface-variant)', fontFamily: 'var(--gs-font-kr)' }} tickLine={false} axisLine={false} minTickGap={20} />
                <YAxis tick={{ fontSize: 11, fill: 'var(--md-sys-color-on-surface-variant)', fontFamily: 'var(--gs-font-kr)' }} tickLine={false} axisLine={false} width={36} />
                <Tooltip
                  contentStyle={{ background: 'var(--md-sys-color-surface-container-lowest)', border: '1px solid var(--md-sys-color-outline-variant)', borderRadius: 12, fontSize: 12, fontFamily: 'var(--gs-font-kr)' }}
                  formatter={(v) => [`${Number(v)}°`, '기온']}
                />
                <Line type="monotone" dataKey="temp" name="기온" stroke="var(--md-sys-color-primary)" strokeWidth={2.5} dot={false} />
              </ComposedChart>
            </ResponsiveContainer>

            <div style={{ maxHeight: 360, overflow: 'auto' }}>
              <div style={{ minWidth: 520 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '64px 56px 1fr 1fr 1fr', padding: '8px 0', borderBottom: '1px solid var(--md-sys-color-outline-variant)', font: '600 12px/16px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface-variant)', position: 'sticky', top: 0, zIndex: 1, background: 'var(--md-sys-color-surface-container-low)' }}>
                  <span>시각</span><span>날씨</span>
                  <span style={{ textAlign: 'right' }}>기온</span>
                  <span style={{ textAlign: 'right' }}>강수확률</span>
                  <span style={{ textAlign: 'right' }}>습도 / 강수량</span>
                </div>
                {rows.map((h, i) => (
                  <div key={h.time} style={{ display: 'grid', gridTemplateColumns: '64px 56px 1fr 1fr 1fr', padding: '8px 0', borderBottom: i === rows.length - 1 ? 0 : '1px solid var(--md-sys-color-outline-variant)', alignItems: 'center' }}>
                    <span className="gs-num" style={{ font: '500 13px/18px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface)' }}>{h.label}</span>
                    <Sym name={CODE_SYM[h.code]} size={20} fill={1} style={{ color: 'var(--md-sys-color-primary)' }} />
                    <span className="gs-num" style={{ textAlign: 'right', font: '600 13px/18px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface)' }}>{h.temp}°</span>
                    <span className="gs-num" style={{ textAlign: 'right', font: '500 13px/18px var(--gs-font-kr)', color: h.pop >= 60 ? 'var(--md-sys-color-primary)' : 'var(--md-sys-color-on-surface-variant)' }}>{h.pop}%</span>
                    <span className="gs-num" style={{ textAlign: 'right', font: '500 13px/18px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface-variant)' }}>{h.reh}% / {h.pcp}mm</span>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function DetailStat({ icon, label, value }: { icon: string; label: string; value: string }): React.ReactElement {
  return (
    <div style={{ padding: '12px 14px', borderRadius: 14, background: 'var(--md-sys-color-surface-container-low)', border: '1px solid var(--md-sys-color-outline-variant)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, font: '500 12px/16px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface-variant)' }}>
        <Sym name={icon} size={14} fill={1} style={{ color: 'var(--md-sys-color-primary)' }} />{label}
      </div>
      <div className="gs-num" style={{ font: '700 18px/22px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface)', marginTop: 4 }}>{value}</div>
    </div>
  );
}
